import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { CustomerInfoForm } from '@app/components/forms/CustomerInfoForm/CustomerInfoForm';
import { TabCard } from '@app/components/layouts/TabCard/TabCard';
import StatusBar from '@app/components/statusBar/statusBar';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { Form, notification } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import CommentsTab from '../common/Comments';
import LabNewTaskForm from './LabNewTaskForm';
import TestsComponent from './TestsComponent';
import LabDocumentChecklist from './DocumentCheckList';
import LabResultsComponent from './LabFinalResults';
import { createLabTask, showLabTask, storeLabTask, updateLabTask, editLabTask } from '@app/api/labs.api';
import { LabApiData } from '@app/models/labs/LabResponse';
import { notificationController } from '@app/controllers/notificationController';
import PaymentsInfo from './PaymentsInfor';
import ProductsTab from './ProductsComponent';
import useApiErrorHandler from '@app/components/Error/ErrorMsgHandling';

interface LabProps {
  id?: string;
  type: 'edit' | 'create' | 'view';
  pageTitle: string;
  editable: boolean;
  setCurrentBreadcrumName: (name: string) => void;
}

export const LabsComponent: React.FC<LabProps> = ({ id, type, setCurrentBreadcrumName }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const [form] = Form.useForm();

  const location = useLocation();
  const defaultTab = location.state?.activeTab || '1';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [lab_code, setLabCode] = useState('New');
  const [divisions, setDivisions] = useState<any[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);
  const [productCategories, ProductCategories] = useState<any[]>([]);
  const [selectedDeoartment, setSelectedDepartment] = useState<any>(null);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [comments, setComments] = useState<any[]>([]);
  const [currentType, setCurrentType] = useState<'edit' | 'create' | 'view'>(type);
  const [wfActions, setWfActions] = useState<string[]>([]);
  const isEditable = currentType === 'create' || currentType === 'edit';
  const labId = id ? parseInt(id) : null;
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userType = user?.usertype;
  const [invoiceData, setInvoiceData] = useState<any[]>([]);
  const [savedProducts, setSavedProducts] = useState<any[]>([]);
  const [total, setTotal] = useState<string | number | null>(null);

  //const [reportData, setReportData] = useState<any[]>([]);
  const handleErrorMessages = useApiErrorHandler();

  const [formData, setFormData] = useState<{
    labNewTaskForm: any;
    labDocumentChecklist: any;
    customerInfo: any;
  }>({
    labNewTaskForm: {},
    labDocumentChecklist: {},
    customerInfo: {},
  });
  const [addedTests, setAddedTests] = useState<any[]>([]);
  const formRefs = {
    customerInfo: useRef<any>(null),
    labNewTaskForm: useRef<any>(null),
    labDocumentChecklist: useRef<any>(null),
  };

  const forms = {
    customerInfo: Form.useForm()[0],
    labNewTaskForm: Form.useForm()[0],
    labDocumentChecklist: Form.useForm()[0],
  };

  useEffect(() => {
    const isDraftOrNew = !status || status === 'DRAFT';
    const showEditBtn = status === 'DRAFT';

    const buttons: Record<string, React.ReactElement | false> = {
      'save-button': isEditable && isDraftOrNew && (
        <BaseButton type="primary" loading={isLoading} size="small" onClick={handleSave}>
          Save
        </BaseButton>
      ),
      'cancel-button': isEditable && isDraftOrNew && (
        <BaseButton onClick={() => navigate(-1)} size="small" style={{ marginRight: '8px' }}>
          {t('Cancel')}
        </BaseButton>
      ),
      'edit-button': currentType === 'view' && showEditBtn && (
        <BaseButton type="primary" loading={isLoading} size="small" onClick={() => setCurrentType('edit')}>
          Edit
        </BaseButton>
      ),
    };

    Object.entries(buttons).forEach(([key, button]) => button && addHeaderButton(key, button));

    return () => {
      // Remove specific buttons that were added
      Object.keys(buttons).forEach((key) => {
        if (buttons[key as keyof typeof buttons]) {
          removeHeaderButton(key);
        }
      });
    };
  }, [currentType, isEditable, addHeaderButton, removeHeaderButton, isLoading, navigate, status, t]);

  const lab_type = localStorage.getItem('selected_lab_type') || 'REGULAR_TEST';

  const fetchedRef = useRef(false);

  useEffect(() => {
    // if (fetchedRef.current) return;
    // fetchedRef.current = true;
    setLoading(true);

    const fetchData = async () => {
      try {
        const apiResponse =
          labId && currentType === 'view'
            ? await showLabTask(labId)
            : labId && currentType === 'edit'
            ? await editLabTask(labId)
            : currentType === 'create'
            ? await createLabTask(lab_type)
            : null;

        if (!(apiResponse && typeof apiResponse === 'object')) {
          console.warn('Invalid API response format');
          return;
        }

        const res = apiResponse as LabApiData;
        console.log('API Response:', res);

        res.department_options?.length && setDepartmentOptions(res.department_options);
        setWfActions(res?.wf_actions ?? []);
        setInvoiceData(res?.lab_request?.invoices ?? []);
        ProductCategories(res?.product_category_options);
        setTotal(res?.lab_request?.invoices?.[0]?.total ?? null);

        console.log('API Response:', res);

        const lab = res.lab_request;
        if (lab) {
          setStatus(lab.status);

          setLabCode(lab.code || 'New');
          setCurrentBreadcrumName(lab.code || 'New');

          const selectedDepartments =
            lab.lab_request_departments
              ?.map((dept) => {
                const id = dept.department_id || dept.division?.id;
                const label = dept.division?.name || '';
                return id ? { value: id, label } : null;
              })
              .filter((item) => item !== null) || [];

          const divisionsData =
            lab.lab_request_departments?.map((dept) => ({
              division: dept.division,
              labRequestDepartmentId: dept.id,
              alreadyAddedTests:
                dept.lab_request_service_categories?.map(
                  (item: {
                    status_label: any;
                    id: number;
                    lab_service_category: any;
                    user_id: number;
                    test_statuses: any[];
                    lab_request_report: any[];
                  }) => ({
                    ...item.lab_service_category,
                    lab_request_service_category_id: item.id,
                    lab_service_category_id: item.lab_service_category.id,
                    status_label: item.lab_service_category.status_label,
                    user_id: item.user_id || null,
                    status: item.test_statuses || [],
                    reportData: item.lab_request_report,
                    status_label_ds: item.status_label,
                  }),
                ) || [],
            })) || [];
          console.log('set divisionsData', divisionsData);
          setDivisions(divisionsData);

          const departmentsInfo =
            res.lab_request?.lab_request_departments?.map((dept) => ({
              id: dept.id,
              name: dept.division?.name || '',
            })) || [];

          setSelectedDepartment(departmentsInfo);

          setUploadedFiles(
            res.lab_request?.lab_request_departments?.flatMap(
              (dept) =>
                dept.lab_request_documents?.map((doc: { id: any; name: any; image_url: any }) => ({
                  id: doc.id,
                  name: doc.name,
                  file: doc.image_url,
                  image_url: doc.image_url,
                  lab_request_department_id: dept.id,
                  department_name: dept.division?.name || '',
                })) || [],
            ) || [],
          );

          if (res?.lab_request?.lab_request_product_categories) {
            const extractedProducts = res.lab_request.lab_request_product_categories.flatMap((category: any) => {
              if (!category.lab_request_products) return [];

              return category.lab_request_products.map((product: any) => ({
                // Category information
                categoryId: category.product_category_id || null,
                categoryName: category.product_category?.name || '',
                categoryCode: category.product_category?.code || '',

                // Product information
                productId: product.product_id || null,
                productName: product.product?.name || '',
                productCode: product.product?.code || '',

                // Varieties information
                varieties: (product.lab_request_product_varieties || []).map((variety: any) => ({
                  id: variety.product_variety_id || null,
                  name: variety.productvariety?.name || '',
                  code: variety.productvariety?.code || '',
                })),
              }));
            });
            setSavedProducts(extractedProducts);
          } else {
            setSavedProducts([]);
          }

          const customerInfoData = {
            id: lab.referenceable.id || '',
            name: lab.referenceable.name || '',
            customer_type: lab.referenceable.customer_type,
            email: lab.referenceable.user?.email || '',
            status: lab.referenceable.user?.status || '',
            address: lab.referenceable.address || '',
            contact_no: lab.referenceable.contact_no || '',
            nic: lab.referenceable.nic || '',
            business_name: lab.additional_info?.business_name || '',
            business_address: lab.additional_info?.business_address || '',
            company_type: lab.additional_info?.company_type || '',
            business_registration_no: lab.additional_info?.business_reg_no || '',
            department_id: lab.referenceable.id?.toString() || '',
            department_code: lab.referenceable.code || '',
            department_name: lab.referenceable.name || '',
            department_address: lab.referenceable.address || '',
            department_description: lab.referenceable.description || '',
            referenceable_label: lab.referenceable_label || '',
          };

          setFormData({
            labNewTaskForm: {
              department_ids: selectedDepartments,
              description: lab.description || '',
            },
            labDocumentChecklist: {},
            customerInfo: customerInfoData,
          });

          forms.customerInfo.setFieldsValue(customerInfoData);
          forms.labNewTaskForm.setFieldsValue({
            department_ids: selectedDepartments,
            description: lab.description || '',
          });
        } else {
          const defaultFormData = {
            labNewTaskForm: {},
            labDocumentChecklist: {},
            customerInfo: { id: res.customer_options?.[0]?.value?.toString() || '' },
          };

          setFormData(defaultFormData);
          forms.customerInfo.setFieldsValue(defaultFormData.customerInfo);
        }
      } catch (error) {
        console.error('Error fetching lab data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentType, status]);

  const handleSave = async () => {
    try {
      const customerInfoValues = await forms.customerInfo.validateFields();

      const lab_type = localStorage.getItem('selected_lab_type') || 'REGULAR_TEST';

      // Get department_ids from multiple sources with fallback
      let departmentIds;

      try {
        // Try to get from form validation first
        const newTaskValues = await forms.labNewTaskForm.validateFields();
        departmentIds = newTaskValues.department_ids;
      } catch (validationError) {
        // If validation fails, get from form field values
        const allTaskFormValues = forms.labNewTaskForm.getFieldsValue();
        departmentIds = allTaskFormValues.department_ids;
      }

      // Fallback to formData if still empty
      if (!departmentIds || departmentIds.length === 0) {
        departmentIds = formData.labNewTaskForm?.department_ids || [];
      }

      let description;
      try {
        const newTaskValues = await forms.labNewTaskForm.validateFields();
        description = newTaskValues.description;
      } catch (validationError) {
        const allTaskFormValues = forms.labNewTaskForm.getFieldsValue();
        description = allTaskFormValues.description || formData.labNewTaskForm?.description || '';
      }

      const transformedDepartmentIds =
        departmentIds?.map((dept: any) => {
          if (typeof dept === 'object' && dept.value !== undefined) {
            return dept.value;
          }
          return dept;
        }) || [];

      const payload = {
        customer_id: customerInfoValues.id,
        department_id: customerInfoValues.department_id,
        lab_request_departments: transformedDepartmentIds,
        description: description,
        type: lab_type,
      };

      console.log('Submitting data:', payload);
      console.log('Department IDs from form:', departmentIds);
      console.log('Transformed department_ids:', transformedDepartmentIds);

      setLoading(true);

      const action = type === 'create' ? storeLabTask(payload) : labId ? updateLabTask(labId, payload) : null;

      action
        ?.then((res: any) => {
          setLoading(false);
          notificationController.success({ message: res.message });
          navigate(`/view-lab-task`, { state: { id: res?.data?.id ?? labId } });
          setCurrentType('view');
        })
        .catch((err: any) => {
          console.error('API Error:', err);
          handleErrorMessages(err);
          setLoading(false);
        });
    } catch (error) {
      console.error('Validation error:', error);
      setLoading(false);
    }
  };

  const tabs = useMemo(
    () => [
      {
        key: '1',
        title: t('Customer Info'),
        content: (
          <CustomerInfoForm
            form={forms.customerInfo}
            type={currentType}
            customerInfo={formData.customerInfo}
            createType="LAB"
            departmentOptions={departmentOptions}
          />
        ),
      },
      {
        key: '2',
        title: t('Task Info'),
        content: (
          <LabNewTaskForm
            form={forms.labNewTaskForm}
            editable={userType === 'ADMIN' && isEditable}
            status={status || 'DRAFT'}
            departmentOptions={departmentOptions}
          />
        ),
      },
      ...(status !== null
        ? [
            {
              key: '3',
              title: t('Document Checklist'),
              content: (
                <LabDocumentChecklist
                  labRequestId={String(labId ?? '')}
                  labRequestDepartmentId={selectedDeoartment ?? []}
                  status={String(status ?? '')}
                  uploadedFiles={uploadedFiles}
                  editable={isEditable}
                />
              ),
            },
          ]
        : []),

      ...(lab_type === 'BI_ANNUAL' && status !== null
        ? [
            {
              key: '4',
              title: t('Consignment Products'),
              content: (
                <ProductsTab
                  lab_id={labId ?? null}
                  categoryOption={productCategories}
                  editable={isEditable}
                  savedProducts={savedProducts}
                />
              ),
            },
          ]
        : []),

      ...(status !== null && status !== 'DRAFT'
        ? [
            {
              key: '5',
              title:
                lab_type === 'VACCINE_MANUFACTURING'
                  ? t('Production Requirement')
                  : lab_type === 'DIRECT_SALES'
                  ? t('Products')
                  : t('Lab Tests'),
              content: (
                <TestsComponent
                  selectedDivisions={divisions}
                  lab_id={labId ?? null}
                  status={status}
                  tr_type={'lab-tests'}
                  status_step={'step-1'}
                  lab_type={lab_type}
                />
              ),
            },
          ]
        : []),

      ...(status !== null &&
      status !== 'DRAFT' &&
      status !== 'PENDING_TA' &&
      status !== 'PENDING_SV' &&
      status !== 'PENDING_PAYMENT' &&
      status !== 'PENDING_MA' &&
      lab_type !== 'DIRECT_SALES'
        ? [
            {
              key: '6',
              title: lab_type === 'VACCINE_MANUFACTURING' ? t('Vaccine Production') : t('Test Results'),
              content: (
                <TestsComponent
                  selectedDivisions={divisions}
                  lab_id={labId ?? null}
                  status={status}
                  tr_type={'test-results'}
                  status_step={'step-2'}
                  lab_type={lab_type}
                />
              ),
            },
          ]
        : []),

      ...(status !== null &&
      status !== 'DRAFT' &&
      status !== 'PENDING_TA' &&
      status !== 'PENDING_MA' &&
      status !== 'PENDING_SV' &&
      status !== 'PENDING_ITR' &&
      status !== 'PENDING_ITRA' &&
      status !== 'PENDING_PAYMENT' &&
      lab_type !== 'VACCINE_MANUFACTURING' &&
      lab_type !== 'DIRECT_SALES'
        ? [
            {
              key: '7',
              title: t('Final Results'),
              content: (
                <LabResultsComponent
                  selectedDivisions={divisions}
                  status={status}
                  lab_id={labId ?? null}
                  tr_type={'final-results'}
                  lab_type={lab_type}
                  status_step={'step-3'}
                />
              ),
            },
          ]
        : []),
      // ...(status !== null && status !== 'DRAFT' &&
      //   ? [
      //       {
      //         key: '8',
      //         title: t('Comments'),
      //         content: <CommentsTab status={status} commentData={comments} pr_id={id ?? null} />,
      //       },
      //     ]
      //   : []),
      ...(status !== null &&
      status !== 'DRAFT' &&
      status !== 'PENDING_TA' &&
      status !== 'PENDING_MA' &&
      status !== 'PENDING_IA'
        ? [
            {
              key: '8',
              title: t('Payments'),
              content: (
                <PaymentsInfo
                  id={id ?? null}
                  invoice_data={invoiceData}
                  lab_code={lab_code ?? null}
                  labStatus={status}
                  wfActions={wfActions || []}
                  total={total}
                />
              ),
            },
          ]
        : []),
    ],
    [currentType, isEditable, status, departmentOptions],
  );

  return (
    <BaseRow gutter={[16, 16]}>
      <BaseCol span={24}>
        <StatusBar
          id={id ?? null}
          status={status}
          setStatus={setStatus}
          wfActions={wfActions || []}
          currentType={currentType}
          workflowType="lab"
          onViewInvoicesClick={() => setActiveTab('8')}
        />
      </BaseCol>
      <BaseCol span={24}>
        <TabCard tabs={tabs} activeKey={activeTab} onChange={setActiveTab} title={lab_code} />
      </BaseCol>
    </BaseRow>
  );
};
